"use client";

import React, { useState, useEffect } from "react";
import { X, MoreVertical, TrendingUp } from "lucide-react";
import BookingDrawer from "../customers/BookingDrawer";

interface Purchase {
  id: number;
  title: string;
  dateLabel: string;
  location: string;
  provider: string;
  items: string;
  price: string;
  status: string;
}

type PurchaseHistoryDrawerProps = {
  isOpen: boolean;
  purchase: Purchase | null;
  onClose: () => void;
  customerName?: string;
  customerEmail?: string;
};

export default function PurchaseHistoryDrawer({
  isOpen,
  purchase,
  onClose,
  customerName,
  customerEmail,
}: PurchaseHistoryDrawerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [activeTab, setActiveTab] = useState<"Details" | "Activity">("Activity");
  const [isBookingDrawerOpen, setIsBookingDrawerOpen] = useState(false);

  useEffect(() => {
    if (isOpen && purchase) {
      setIsVisible(true);
      setIsClosing(false);
      setActiveTab("Activity");
    }
  }, [isOpen, purchase]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleRebook = () => {
    // Start closing animation for purchase history drawer
    setIsClosing(true);

    // Wait for drawer to fully close, then open booking drawer
    setTimeout(() => {
      setIsVisible(false);
      setIsBookingDrawerOpen(true);
    }, 300);
  };

  const handleCloseBookingDrawer = () => {
    // Close booking drawer
    setIsBookingDrawerOpen(false);

    // Re-open purchase history drawer after a brief delay
    setTimeout(() => {
      setIsVisible(true);
      setIsClosing(false);
    }, 100);
  };

  if (!isOpen && !isVisible && !isBookingDrawerOpen) return null;

  const services = [
    {
      name: "Balayage",
      duration: "2h 30min",
      provider: "Amir Mukhtar",
      price: 150,
    },
    {
      name: "Blow Dry",
      duration: "35min",
      provider: "Amir Mukhtar",
      price: 35,
    },
    {
      name: "Hair Color",
      duration: "1h 15min",
      provider: "Amir Mukhtar",
      price: 57,
    },
    {
      name: "Haircut",
      duration: "45min",
      provider: "Amir Mukhtar",
      price: 40,
    },
  ];

  const subtotal = services.reduce((sum, service) => sum + service.price, 0);
  const tip = 28.2;
  const total = subtotal + tip;

  return (
    <>
      {isVisible && !isBookingDrawerOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 ${isVisible && !isClosing ? "opacity-100" : "opacity-0"
              }`}
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Drawer Panel */}
          <div
            className={`relative w-full lg:w-[85vw] max-w-5xl h-screen transform transition-transform duration-300 ease-out flex origin-right ${isVisible && !isClosing ? "translate-x-0" : "translate-x-full"
              }`}
          >
            {/* Standardized Close Button (Matches QuickPaymentPanel style) */}
            <button
              onClick={handleClose}
              className="absolute lg:-left-16 right-4 top-4 lg:right-auto lg:top-10 w-10 h-10 lg:w-11 lg:h-11 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-[-4px_0_12px_rgba(0,0,0,0.1)] lg:shadow-md hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
              aria-label="Close"
            >
              <X size={22} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
            </button>

            {/* Drawer Content */}
            <div className="relative z-50 w-full h-screen bg-white shadow-2xl flex flex-col lg:flex-row overflow-hidden">
              {/* Sidebar/Top Navigation */}
              <div className="w-full lg:w-[90px] border-b lg:border-b-0 lg:border-r border-gray-200 flex flex-row lg:flex-col items-center justify-center lg:justify-start py-2 lg:py-6 gap-2 lg:gap-8 bg-gray-50/50 lg:bg-white flex-shrink-0">
                <button
                  onClick={() => setActiveTab("Details")}
                  className={`flex flex-row lg:flex-col items-center gap-2 px-6 lg:px-4 py-3 rounded-xl lg:rounded-lg transition-all ${activeTab === "Details"
                    ? "text-[#885ABB] bg-white lg:bg-transparent shadow-sm lg:shadow-none"
                    : "text-gray-400 hover:text-gray-600"
                    }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${activeTab === "Details" ? "bg-purple-100" : "bg-gray-200/50"
                      }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <span className="text-[10px] lg:text-xs font-black uppercase tracking-wider">Details</span>
                </button>

                <button
                  onClick={() => setActiveTab("Activity")}
                  className={`flex flex-row lg:flex-col items-center gap-2 px-6 lg:px-4 py-3 rounded-xl lg:rounded-lg transition-all ${activeTab === "Activity"
                    ? "text-[#885ABB] bg-white lg:bg-transparent shadow-sm lg:shadow-none"
                    : "text-gray-400 hover:text-gray-600"
                    }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${activeTab === "Activity" ? "bg-purple-100" : "bg-gray-200/50"
                      }`}
                  >
                    <TrendingUp className="w-5 h-5" strokeWidth={2.5} />
                  </div>
                  <span className="text-[10px] lg:text-xs font-black uppercase tracking-wider">Activity</span>
                </button>
              </div>

              {/* Main Content */}
              <div className="flex-1 flex flex-col">
                {/* Header */}
                <div className="px-5 lg:px-8 py-5 lg:py-6 border-b border-gray-200 bg-white">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 gap-3 lg:gap-0">
                    <div className="flex items-center gap-3 mt-8 lg:mt-0">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full border border-green-100">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-xs font-bold uppercase tracking-wider">Completed</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleRebook}
                        className="flex-1 sm:flex-none px-6 py-2 bg-[#885ABB] text-white text-sm font-bold rounded-xl hover:bg-[#724a9d] transition shadow-md active:scale-95"
                      >
                        Rebook
                      </button>
                      <button className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition border border-gray-100">
                        <MoreVertical className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  <h2 className="text-2xl lg:text-3xl font-black text-gray-900 mb-2 tracking-tight">
                    Purchase History
                  </h2>
                  <p className="text-gray-500 text-sm font-medium">
                    Thu 25 Sep 2025 · Beauty Parlor
                  </p>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto px-5 lg:px-8 py-6 bg-gray-50/30">
                  {activeTab === "Details" && (
                    <div className="space-y-6">
                      {/* Customer Card */}
                      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-[#885ABB] text-lg font-bold shadow-inner">
                            {customerName?.charAt(0) || "C"}
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-lg font-bold text-gray-900 truncate">
                              {customerName || "Customer"}
                            </h3>
                            <p className="text-sm text-gray-500 font-medium truncate">
                              {customerEmail || "-"}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Sale Details */}
                      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                        <div className="mb-6 flex justify-between items-start">
                          <div>
                            <h3 className="text-xl font-black text-gray-900 tracking-tight">
                              Sale #3
                            </h3>
                            <p className="text-sm text-gray-500 font-medium mt-1">
                              Thu 25 Sep 2025
                            </p>
                          </div>
                          <div className="p-2 bg-purple-50 rounded-xl">
                            <svg className="w-6 h-6 text-[#885ABB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                          </div>
                        </div>

                        {/* Services List */}
                        <div className="space-y-4 mb-8">
                          {services.map((service, idx) => (
                            <div
                              key={idx}
                              className="flex items-start justify-between py-4 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50 -mx-2 px-2 rounded-lg transition-colors"
                            >
                              <div className="flex-1 min-w-0 pr-4">
                                <h4 className="font-bold text-gray-800">
                                  {service.name}
                                </h4>
                                <p className="text-xs text-gray-500 font-medium mt-1">
                                  {service.duration} · {service.provider}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-black text-[#885ABB]">
                                  PKR {service.price}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Totals */}
                        <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
                          <div className="flex justify-between text-gray-600 font-bold text-sm">
                            <span>Subtotal</span>
                            <span>PKR {subtotal.toFixed(2)}</span>
                          </div>
                          <div className="border-t border-gray-200 pt-4">
                            <div className="flex justify-between items-end">
                              <div>
                                <p className="font-black text-gray-900 text-xl tracking-tight">
                                  Total
                                </p>
                                <p className="text-xs text-gray-500 font-bold mt-1">
                                  Tip to Amir
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-black text-[#885ABB] text-2xl">
                                  PKR {total.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-400 font-black">
                                  PKR {tip.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "Activity" && (
                    <div className="max-w-3xl mx-auto">
                      {/* Timeline */}
                      <div className="relative">
                        {/* Vertical Line */}
                        <div className="absolute left-[19px] top-0 bottom-0 w-1 bg-gray-100 rounded-full" />

                        {/* Activity Item */}
                        <div className="relative pl-12 sm:pl-16 pb-8">
                          {/* Timeline Dot */}
                          <div className="absolute left-0 top-0 w-10 h-10 rounded-full bg-white border-2 border-purple-200 flex items-center justify-center shadow-sm z-10">
                            <div className="w-3 h-3 rounded-full bg-[#885ABB]" />
                          </div>

                          {/* Content Card */}
                          <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 gap-4">
                              <div className="min-w-0 pr-4">
                                <h4 className="text-lg font-black text-gray-900 mb-1 tracking-tight">
                                  Sale 3 created
                                </h4>
                                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">
                                  25 Sep 2025 at 9:17pm
                                </p>
                              </div>
                              <div className="flex items-center gap-3 self-end sm:self-auto">
                                <div className="w-10 h-10 rounded-full overflow-hidden bg-purple-100 border-2 border-white shadow-sm">
                                  <img
                                    src="https://api.dicebear.com/7.x/avataaars/svg?seed=Amir"
                                    alt="Amir Mukhtar"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="w-6 h-6 rounded-full bg-[#22C55E] border-2 border-white flex items-center justify-center -ml-3 shadow-sm">
                                  <svg
                                    className="w-3 h-3 text-white"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-xl">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#885ABB]" />
                              <p className="text-sm font-bold text-gray-700">
                                Completed by Amir Mukhtar
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Activity Summary */}
                      <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                          Activity for this sale in the last 90 days
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Drawer */}
      {isBookingDrawerOpen && (
        <BookingDrawer
          isOpen={isBookingDrawerOpen}
          customerName={customerName || "Customer"}
          customerEmail={customerEmail || ""}
          customerInitial={customerName?.charAt(0) || "C"}
          onClose={handleCloseBookingDrawer}
        />
      )}
    </>
  );
}
